import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Loader2, RefreshCw, ExternalLink, Sparkles, CheckCircle2, AlertCircle, MapPin, BookOpen, Wand2, Trash2, PenLine,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getBlogCategories } from "@/lib/api";
import {
  getBlogOverview, generateBlog, createCustomBlog, regenerateCustomBlog, deleteBlog, getJob,
} from "@/lib/adminApi";

const StatusCell = ({ ok }) =>
  ok ? (
    <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-4 h-4" /> Hazır</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-amber-400"><AlertCircle className="w-4 h-4" /> Eksik</span>
  );

function PostsTable({ rows, firstColLabel, rowKey, onRegen, onDelete, rowLoading, testId, showCategory = false }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-white/10" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="bg-[#141517] text-[#9A9387] text-left">
          <tr>
            <th className="p-3 font-medium">{firstColLabel}</th>
            {showCategory && <th className="p-3 font-medium">Kategori</th>}
            <th className="p-3 font-medium">Başlık</th>
            <th className="p-3 font-medium">Kelime</th>
            <th className="p-3 font-medium">Durum</th>
            <th className="p-3 font-medium text-right">İşlem</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/8">
          {rows.map((it) => (
            <tr key={it.slug} className="bg-[#0E0F10] hover:bg-[#141517] text-[#EDE7DA]">
              <td className={`p-3 font-medium ${showCategory ? "max-w-[300px]" : "whitespace-nowrap"}`} title={it.name}>
                <span className={showCategory ? "line-clamp-2" : ""}>{it.name}</span>
              </td>
              {showCategory && (
                <td className="p-3 whitespace-nowrap">
                  <span className="rounded-full border border-[#C8A24A]/40 text-[#D7B86A] text-[11px] px-2.5 py-0.5">{it.category_name}</span>
                </td>
              )}
              <td className="p-3 max-w-[320px] truncate text-[#B8B1A5]" title={it.title || ""}>{it.title || "—"}</td>
              <td className="p-3">{it.word_count || 0}</td>
              <td className="p-3"><StatusCell ok={it.has_post} /></td>
              <td className="p-3">
                <div className="flex items-center justify-end gap-3">
                  {it.has_post && (
                    <Link to={`/blog/${it.blog_slug}`} target="_blank" className="text-[#9A9387] hover:text-white" title="Gör">
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  )}
                  <Button size="sm" variant="outline" disabled={rowLoading === `${rowKey}:${it.slug}`}
                    onClick={() => onRegen(it.slug)} data-testid={`regen-${rowKey}-button`}
                    className="h-8 border-white/15 text-[#EDE7DA] bg-transparent hover:bg-white/5">
                    {rowLoading === `${rowKey}:${it.slug}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (it.has_post ? "Yeniden Üret" : "Üret")}
                  </Button>
                  {onDelete && (
                    <Button size="sm" variant="outline" onClick={() => onDelete(it.slug)} data-testid={`delete-${rowKey}-button`}
                      className="h-8 border-white/15 text-[#EDE7DA] bg-transparent hover:bg-red-500/10 hover:text-red-300" title="Sil">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Custom topic generator: form -> background job -> poll until done. */
function CustomTopicForm({ onDone, activeJob, setActiveJob }) {
  const { data: catData } = useQuery({ queryKey: ["blog-categories"], queryFn: getBlogCategories, staleTime: 600000 });
  const categories = (catData && catData.items) || [];
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("dugun-rehberi");
  const [keywords, setKeywords] = useState("");
  const [brief, setBrief] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 8) {
      toast.error("Başlık en az 8 karakter olmalı");
      return;
    }
    setSubmitting(true);
    try {
      const res = await createCustomBlog({ title: title.trim(), category, keywords: keywords.trim() || null, brief: brief.trim() || null });
      setActiveJob(res.job);
      toast.success("Üretim başladı — yaklaşık 1 dakika sürer");
      setTitle(""); setKeywords(""); setBrief("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Başlatılamadı");
    } finally {
      setSubmitting(false);
    }
  };

  const busy = Boolean(activeJob && (activeJob.status === "pending" || activeJob.status === "running"));

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#C8A24A]/30 bg-[#141517] p-5 sm:p-6" data-testid="custom-topic-form">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-2xl text-[#F7F2E8]"><Wand2 className="w-5 h-5 text-[#C8A24A]" /> Özel Konu Üret</h2>
          <p className="text-xs text-[#9A9387] mt-1">Bir başlık yazın; yapay zeka SEO uyumlu, 900+ kelimelik Türkçe bir blog yazısı üretip yayına alsın.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <div className="md:col-span-2">
          <Label htmlFor="ct-title" className="text-[#B8B1A5] text-xs">Başlık *</Label>
          <Input id="ct-title" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={160} required
            placeholder="örn. Düğün Fotoğrafçısına Ne Zaman Rezervasyon Yapılmalı?"
            className="mt-1 bg-[#0E0F10] border-white/15 text-[#EDE7DA]" data-testid="custom-topic-title" />
        </div>
        <div>
          <Label className="text-[#B8B1A5] text-xs">Kategori</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="mt-1 bg-[#0E0F10] border-white/15 text-[#EDE7DA]" data-testid="custom-topic-category">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(categories.length ? categories : [{ slug: "dugun-rehberi", name: "Düğün Rehberi" }])
                .filter((c) => c.slug !== "ilce-rehberi")
                .map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ct-kw" className="text-[#B8B1A5] text-xs">Anahtar kelimeler (virgülle, opsiyonel)</Label>
          <Input id="ct-kw" value={keywords} onChange={(e) => setKeywords(e.target.value)} maxLength={400}
            placeholder="düğün fotoğrafçısı rezervasyon, erken rezervasyon"
            className="mt-1 bg-[#0E0F10] border-white/15 text-[#EDE7DA]" data-testid="custom-topic-keywords" />
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="ct-brief" className="text-[#B8B1A5] text-xs">Kısa yönlendirme / işlenecek başlıklar (opsiyonel, satır satır)</Label>
          <Textarea id="ct-brief" value={brief} onChange={(e) => setBrief(e.target.value)} maxLength={1200} rows={3}
            placeholder={"Yaz sezonu yoğunluğu\n6-12 ay önce planlama\nKapora ve sözleşme"}
            className="mt-1 bg-[#0E0F10] border-white/15 text-[#EDE7DA]" data-testid="custom-topic-brief" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-5">
        <Button type="submit" disabled={submitting || busy} className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold" data-testid="custom-topic-submit">
          {submitting || busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {busy ? "Üretiliyor..." : "Yazıyı Üret"}
        </Button>
        {activeJob && (
          <span className="text-sm" data-testid="custom-job-status">
            {activeJob.status === "done" && (
              <span className="inline-flex items-center gap-1 text-emerald-400">
                <CheckCircle2 className="w-4 h-4" /> Hazır: <Link to={`/blog/${activeJob.slug}`} target="_blank" className="underline">{activeJob.title}</Link>
                {activeJob.word_count ? ` (${activeJob.word_count} kelime)` : ""}
              </span>
            )}
            {activeJob.status === "failed" && <span className="text-red-400">Hata: {activeJob.error}</span>}
            {(activeJob.status === "pending" || activeJob.status === "running") && (
              <span className="text-[#D7B86A]">"{activeJob.title}" yazılıyor… bu sayfada bekleyebilir veya başka işlere devam edebilirsiniz.</span>
            )}
          </span>
        )}
      </div>
    </form>
  );
}

export default function BlogPanel() {
  const qc = useQueryClient();
  const [genAll, setGenAll] = useState(false);
  const [rowLoading, setRowLoading] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const pollRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-blog-overview"],
    queryFn: getBlogOverview,
    refetchInterval: genAll ? 5000 : false,
  });
  const items = (data && data.items) || [];
  const topics = (data && data.topics) || [];
  const custom = (data && data.custom) || [];
  const generated = data ? data.generated : 0;
  const total = data ? data.total : 0;
  const districts = (data && data.districts) || { total: 25, generated: 0 };
  const topicsSummary = (data && data.topics_summary) || { total: 0, generated: 0 };

  // poll the active custom job
  useEffect(() => {
    if (!activeJob || !(activeJob.status === "pending" || activeJob.status === "running")) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const j = await getJob(activeJob.id);
        setActiveJob(j);
        if (j.status === "done" || j.status === "failed") {
          clearInterval(pollRef.current);
          if (j.status === "done") toast.success("Yazı hazır ve yayında");
          else toast.error("Üretim başarısız: " + (j.error || ""));
          qc.invalidateQueries({ queryKey: ["admin-blog-overview"] });
          qc.invalidateQueries({ queryKey: ["blog"] });
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [activeJob?.id, activeJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const regen = async (kind, slug) => {
    setRowLoading(`${kind}:${slug}`);
    try {
      if (kind === "custom") {
        const res = await regenerateCustomBlog(slug);
        setActiveJob(res.job);
        toast.success("Yeniden üretim başladı");
      } else {
        const body = kind === "topic" ? { topic_slug: slug, force: true } : { district_slug: slug, force: true };
        await generateBlog(body);
        toast.success("Yazı üretildi");
      }
      qc.invalidateQueries({ queryKey: ["admin-blog-overview"] });
      qc.invalidateQueries({ queryKey: ["blog"] });
    } catch {
      toast.error("Üretim başarısız (tekrar deneyin)");
    } finally {
      setRowLoading(null);
    }
  };

  const removeCustom = async (slug) => {
    if (!window.confirm("Bu özel yazı kalıcı olarak silinsin mi?")) return;
    try {
      await deleteBlog(slug);
      toast.success("Yazı silindi");
      qc.invalidateQueries({ queryKey: ["admin-blog-overview"] });
      qc.invalidateQueries({ queryKey: ["blog"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    }
  };

  const genMissing = async () => {
    try {
      await generateBlog({ force: false });
      setGenAll(true);
      toast.success("Eksik yazılar arka planda üretiliyor...");
      setTimeout(() => setGenAll(false), 300000);
    } catch {
      toast.error("Başlatılamadı");
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <div className="rounded-xl bg-[#141517] border border-white/10 px-5 py-3" data-testid="blog-total-counter">
            <span className="text-3xl font-display text-[#D7B86A]">{generated}</span>
            <span className="text-[#9A9387]"> / {total}</span>
            <div className="text-xs uppercase tracking-wide text-[#9A9387]">Toplam Yazı</div>
          </div>
          <div className="rounded-xl bg-[#141517] border border-white/10 px-5 py-3" data-testid="blog-district-counter">
            <span className="text-2xl font-display text-[#EDE7DA]">{districts.generated}</span>
            <span className="text-[#9A9387]"> / {districts.total}</span>
            <div className="text-xs uppercase tracking-wide text-[#9A9387]">İlçe Rehberi</div>
          </div>
          <div className="rounded-xl bg-[#141517] border border-white/10 px-5 py-3" data-testid="blog-topic-counter">
            <span className="text-2xl font-display text-[#EDE7DA]">{topicsSummary.generated}</span>
            <span className="text-[#9A9387]"> / {topicsSummary.total}</span>
            <div className="text-xs uppercase tracking-wide text-[#9A9387]">Genel Yazı</div>
          </div>
          <div className="rounded-xl bg-[#141517] border border-white/10 px-5 py-3" data-testid="blog-custom-counter">
            <span className="text-2xl font-display text-[#EDE7DA]">{custom.length}</span>
            <div className="text-xs uppercase tracking-wide text-[#9A9387]">Özel Yazı</div>
          </div>
          {genAll && (
            <div className="flex items-center gap-2 text-sm text-[#D7B86A]">
              <Loader2 className="w-4 h-4 animate-spin" /> Üretim sürüyor...
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button onClick={genMissing} disabled={genAll || (total > 0 && generated >= total)} data-testid="generate-missing-button"
            className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold">
            <Sparkles className="w-4 h-4 mr-2" /> Eksikleri Üret
          </Button>
          <Button variant="outline" onClick={() => qc.invalidateQueries({ queryKey: ["admin-blog-overview"] })}
            className="border-white/15 text-[#EDE7DA] bg-transparent hover:bg-white/5">
            <RefreshCw className="w-4 h-4 mr-2" /> Yenile
          </Button>
        </div>
      </div>

      <div className="mb-10">
        <CustomTopicForm activeJob={activeJob} setActiveJob={setActiveJob} />
      </div>

      {isLoading ? (
        <div className="py-20 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8A24A]" /></div>
      ) : (
        <div className="space-y-10">
          <section>
            <h2 className="flex items-center gap-2 font-display text-2xl text-[#F7F2E8] mb-3">
              <PenLine className="w-5 h-5 text-[#C8A24A]" /> Özel Yazılar
              <span className="text-sm font-sans text-[#9A9387]">({custom.length})</span>
            </h2>
            {custom.length === 0 ? (
              <div className="rounded-xl border border-white/10 bg-[#141517] p-6 text-sm text-[#9A9387]" data-testid="custom-empty">
                Henüz özel konu yazısı yok. Yukarıdaki formdan bir başlık girerek ilk yazınızı üretin.
              </div>
            ) : (
              <PostsTable rows={custom} firstColLabel="Başlık" rowKey="custom" showCategory
                onRegen={(slug) => regen("custom", slug)} onDelete={removeCustom} rowLoading={rowLoading} testId="custom-overview-table" />
            )}
          </section>

          <section>
            <h2 className="flex items-center gap-2 font-display text-2xl text-[#F7F2E8] mb-3">
              <BookOpen className="w-5 h-5 text-[#C8A24A]" /> Genel Yazılar
              <span className="text-sm font-sans text-[#9A9387]">({topicsSummary.generated}/{topicsSummary.total})</span>
            </h2>
            <PostsTable rows={topics} firstColLabel="Konu" rowKey="topic" showCategory
              onRegen={(slug) => regen("topic", slug)} rowLoading={rowLoading} testId="topic-overview-table" />
          </section>

          <section>
            <h2 className="flex items-center gap-2 font-display text-2xl text-[#F7F2E8] mb-3">
              <MapPin className="w-5 h-5 text-[#C8A24A]" /> İlçe Rehberleri
              <span className="text-sm font-sans text-[#9A9387]">({districts.generated}/{districts.total})</span>
            </h2>
            <PostsTable rows={items} firstColLabel="İlçe" rowKey="blog"
              onRegen={(slug) => regen("district", slug)} rowLoading={rowLoading} testId="blog-overview-table" />
          </section>
        </div>
      )}
      <p className="text-xs text-[#7C766B] mt-4">
        Not: Tek bir yazının üretimi ~30-60 saniye sürebilir. "Eksikleri Üret" arka planda çalışır; liste otomatik yenilenir.
      </p>
    </div>
  );
}
